/**
 * Zod discriminated union covering every chat SSE event type, mirroring
 * `backend/src/chat/sse_events.py`'s 22 Pydantic models field-for-field.
 *
 * `frontend/src/lib/chat.ts` is a CONSUMER of this module (its `parseSseBlock`
 * runs every parsed SSE block through `chatSseEventSchema` before treating it
 * as a `ChatStreamEvent`), not a producer — the two `report_*` schemas that
 * used to live inline in `chat.ts` are defined here instead, and `chat.ts`
 * imports them back. See openspec/changes/type-chat-sse-events/design.md.
 *
 * Zero `any` anywhere in this file, per CLAUDE.md rule #2.
 */

import { z } from "zod";
import { assetLocatorSchema } from "./asset-locator";
import { reportRefSchema } from "./reports";
import { storyPreparationRefSchema } from "./story-preparation-ref";

const reasoningLevelValueSchema = z.enum(["off", "low", "medium", "high"]);

export const sessionEventSchema = z
  .object({
    type: z.literal("session").default("session"),
    session_id: z.string(),
    user_message_id: z.string().optional(),
    assistant_message_id: z.string().optional(),
  })
  .strict();

export const retrievalSummaryEventSchema = z
  .object({
    type: z.literal("retrieval_summary").default("retrieval_summary"),
    vector_hits: z.number(),
    bm25_hits: z.number(),
    graph_hits: z.number(),
    sparse_hits: z.number(),
    fused_count: z.number(),
    // Emitted by the backend today but not part of the documented minimal
    // shape — kept optional so a strict schema still accepts the actual
    // wire payload (see `sse_events.py::RetrievalSummaryEvent`).
    reranked: z.boolean().optional(),
    meta_intent: z.string().optional(),
    skipped: z.boolean().optional(),
  })
  .strict();

export const reasoningLevelEventSchema = z
  .object({
    type: z.literal("reasoning_level").default("reasoning_level"),
    level: reasoningLevelValueSchema,
  })
  .strict();

export const tokenEventSchema = z
  .object({
    type: z.literal("token").default("token"),
    content: z.string(),
  })
  .strict();

export const thinkingEventSchema = z
  .object({
    type: z.literal("thinking").default("thinking"),
    content: z.string(),
  })
  .strict();

export const usageEventSchema = z
  .object({
    type: z.literal("usage").default("usage"),
    prompt_tokens: z.number(),
    completion_tokens: z.number(),
    total_tokens: z.number(),
    cache_read_input_tokens: z.number().optional(),
    cache_creation_input_tokens: z.number().optional(),
    cost_usd: z.number().optional(),
  })
  .strict();

export const citationEventSchema = z
  .object({
    type: z.literal("citation").default("citation"),
    id: z.string(),
    title: z.string(),
    preview: z.string(),
    user_metadata: z.string().nullable().optional(),
    source: z.string().optional(),
    source_ref: z.string().optional(),
    external_url: z.string().optional(),
  })
  .strict();

export const learnedFactEventSchema = z
  .object({
    type: z.literal("learned_fact").default("learned_fact"),
    knowledge_item_id: z.string(),
    auto_approved: z.boolean().optional(),
  })
  .strict();

export const factProposalEventSchema = z
  .object({
    type: z.literal("fact_proposal").default("fact_proposal"),
    draft_id: z.string(),
    assistant_message_id: z.string(),
    text: z.string(),
    confidence: z.number(),
  })
  .strict();

export const userMemoryDraftOperationSchema = z.enum(["create", "update", "delete"]);

export const userMemoryDraftEventSchema = z
  .object({
    type: z.literal("user_memory_draft").default("user_memory_draft"),
    draft_id: z.string(),
    assistant_message_id: z.string(),
    operation: userMemoryDraftOperationSchema,
    text: z.string().optional(),
    target_id: z.string().optional(),
    target_text: z.string().optional(),
  })
  .strict();

export const stageAccessRequestEventSchema = z
  .object({
    type: z.literal("stage_access_request").default("stage_access_request"),
    stage: z.enum(["demo", "dev"]),
    status: z.literal("pending"),
  })
  .strict();

export const toolUseEventSchema = z
  .object({
    type: z.literal("tool_use").default("tool_use"),
    id: z.string(),
    name: z.string(),
    input: z.record(z.string(), z.unknown()),
  })
  .strict();

export const toolResultEventSchema = z
  .object({
    type: z.literal("tool_result").default("tool_result"),
    tool_use_id: z.string(),
    ok: z.boolean(),
    content_preview: z.string(),
    bytes: z.number(),
    error: z.string().optional(),
    ref: z.string().optional(),
    stage: z.string().optional(),
    ref_fallback: z.boolean().optional(),
    tried_refs: z.array(z.string()).optional(),
    knowledge_lifecycle: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

// One provider call of a step (`StepCall` in `step_usage.py`). The backend
// drops `label`/`outcome`/`reason`/`confidences`/`answer` when unset but keeps
// `latency_ms` and `cost_usd` even when null. `cost_usd` is a decimal string.
// `answer` maps a closed question id to the decided value (`level`/`intent`
// strings, `needs_retrieval`/`needs_graph` booleans); never query text.
export const stepCallWireSchema = z
  .object({
    kind: z.enum(["decision_engine", "llm"]),
    provider: z.string(),
    label: z.string().optional(),
    model: z.string(),
    latency_ms: z.number().nullable().optional(),
    prompt_tokens: z.number(),
    completion_tokens: z.number(),
    cost_usd: z.string().nullable(),
    outcome: z.enum(["served", "fell_through", "failed"]).optional(),
    reason: z.string().optional(),
    confidences: z.record(z.string(), z.number()).optional(),
    answer: z.record(z.string(), z.union([z.string(), z.boolean()])).optional(),
  })
  .strict();

// `StepUsageWire`'s wire shape (see `chat.ts:1606-1632`) — duplicated here
// (not imported) because `chat.ts` is a consumer of this module, not the
// other way around.
const stepUsageWireSchema = z
  .object({
    slot: z.string().optional(),
    model: z.string(),
    prompt_tokens: z.number(),
    completion_tokens: z.number(),
    total_tokens: z.number(),
    cost_usd: z.string().nullable(),
    cost_source: z.enum(["computed", "unknown"]),
    cache_read_input_tokens: z.number().optional(),
    cache_creation_input_tokens: z.number().optional(),
    fallback_used: z.boolean().optional(),
    calls: z.array(stepCallWireSchema).optional(),
  })
  .strict();

// `detail` stays a loose, open-ended record: the backend's `StepEvent.detail`
// (`sse_events.py`) is itself `dict[str, object]`, not a closed shape — see
// that model's docstring for why.
const stepDetailSchema = z.record(z.string(), z.unknown());

export const stepEventSchema = z
  .object({
    type: z.literal("step").default("step"),
    id: z.string(),
    status: z.enum(["start", "done", "error"]),
    elapsed_ms: z.number().optional(),
    detail: stepDetailSchema.optional(),
    usage: stepUsageWireSchema.optional(),
  })
  .strict();

export const citationWarningEventSchema = z
  .object({
    type: z.literal("citation_warning").default("citation_warning"),
    fabricated_markers: z.array(z.string()),
  })
  .strict();

export const sourceRedactionWarningEventSchema = z
  .object({
    type: z.literal("source_redaction_warning").default("source_redaction_warning"),
    redacted_spans: z.array(z.number()),
  })
  .strict();

export const userMemoryAppliedEventSchema = z
  .object({
    type: z.literal("user_memory_applied").default("user_memory_applied"),
    count: z.number(),
    texts: z.array(z.string()).optional(),
    ids: z.array(z.string()).optional(),
  })
  .strict();

export const truncatedEventSchema = z
  .object({
    type: z.literal("truncated").default("truncated"),
    reason: z.enum(["max_tokens", "refusal"]),
  })
  .strict();

export const reportReadyEventSchema = reportRefSchema
  .extend({
    type: z.literal("report_ready").default("report_ready"),
    status: z.enum(["ready", "partial"]),
    assistant_message_id: z.guid(),
  })
  .strict();

export const storyPreparationReadyEventSchema = storyPreparationRefSchema
  .extend({
    type: z.literal("story_preparation_ready").default("story_preparation_ready"),
  })
  .strict();

export const reportPublishFailedEventSchema = z
  .object({
    type: z.literal("report_publish_failed").default("report_publish_failed"),
    // The backend always sends this key (never omits it — see
    // `sse_events.py::ReportPublishFailedEvent`, no default), including an
    // explicit `null`, so this is required-but-nullable rather than
    // optional. No `.transform()`: `z.discriminatedUnion` requires each
    // member to stay a plain `ZodObject`.
    trigger: z.enum(["edit", "explicit", "background", "autonomous"]).nullable(),
    reason: z.enum(["publication_failed", "control_not_published"]),
  })
  .strict();

export const doneEventSchema = z
  .object({
    type: z.literal("done").default("done"),
    citation_count: z.number(),
    assistant_message_id: z.string().optional(),
    answer_kind: z.string().optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Asset locators (return-knowledge-images-across-surfaces) — a discriminated
// union on `kind`, mirroring `backend/src/knowledge_assets/contracts.py`'s
// `AssetLocator` union. `kind` carries a Pydantic default on every member
// (like `type` on every event above), so it is NEVER in that member's
// `required` list — `.default()` here reproduces that, not `.optional()`
// alone, which would leave `kind` absent from the union's discriminant.
// None of these are part of the top-level `ChatSseEvent` union (they nest
// inside `ResponseAssetProjection.locator`), so the contract-parity test
// never inspects them directly — parity there is anchored on
// `ResponseAssetsEvent` alone, whose `assets` field only cares that each
// item round-trips through this schema without loss.
//
// Moved to `./asset-locator` (PR 4) so `reports.ts` can reuse the same
// locator union for report image relations without a circular import (this
// module already imports `reportRefSchema` from `./reports`). Re-exported
// here unchanged so every existing consumer of
// `@/lib/chat-event-schemas`'s locator exports is unaffected.
// ---------------------------------------------------------------------------

export * from "./asset-locator";

/**
 * Safe DTO shared by the `response_assets` SSE event and chat-history reads
 * (`ChatMessageRead.response_assets`) — mirrors
 * `backend/src/knowledge_assets/contracts.py::ResponseAssetProjection`.
 * No bytes, no URL, no storage key, no global asset id: `relation_id` only
 * identifies the owning `chat_message_assets` row, meaningless outside the
 * session that produced it, and is what the authorized byte routes resolve
 * against.
 */
export const responseAssetProjectionSchema = z
  .object({
    relation_id: z.guid(),
    alt_text: z.string().min(1).max(500),
    caption: z.string().min(1).max(4000),
    citation_label: z.string().min(1).max(32),
    locator: assetLocatorSchema,
    preview_available: z.boolean(),
    original_available: z.boolean(),
  })
  .strict();

export type ResponseAssetProjection = z.infer<typeof responseAssetProjectionSchema>;

export const responseAssetsEventSchema = z
  .object({
    type: z.literal("response_assets").default("response_assets"),
    assistant_message_id: z.string(),
    assets: z.array(responseAssetProjectionSchema).min(1),
  })
  .strict();

export const errorEventSchema = z
  .object({
    type: z.literal("error").default("error"),
    message: z.string(),
    code: z.string().optional(),
  })
  .strict();

export const chatSseEventSchema = z.discriminatedUnion("type", [
  sessionEventSchema,
  retrievalSummaryEventSchema,
  reasoningLevelEventSchema,
  tokenEventSchema,
  thinkingEventSchema,
  usageEventSchema,
  citationEventSchema,
  learnedFactEventSchema,
  factProposalEventSchema,
  userMemoryDraftEventSchema,
  stageAccessRequestEventSchema,
  toolUseEventSchema,
  toolResultEventSchema,
  stepEventSchema,
  citationWarningEventSchema,
  sourceRedactionWarningEventSchema,
  userMemoryAppliedEventSchema,
  truncatedEventSchema,
  reportReadyEventSchema,
  reportPublishFailedEventSchema,
  storyPreparationReadyEventSchema,
  doneEventSchema,
  errorEventSchema,
  responseAssetsEventSchema,
]);

export type ChatSseEventParsed = z.infer<typeof chatSseEventSchema>;
